import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import GraduationRegisterData from "./GraduationRegisterData";
import SchoolCatalogueData from "./SchoolCatalogueData";
import MemberDirectoryData from "./MemberDirectoryData";
import SchoolHistoryData from "./SchoolHistoryData";
import YearBookData from "./YearBookData";
import OtherData from "./OtherData";
import {Link, useNavigate} from "react-router-dom";
import {useRecoilValue, useSetRecoilState} from "recoil";
import {selectedSchool} from "../../../recoils/recoil";

const SchoolCatalogue = () => {
    const selectedSchoolList = useRecoilValue(selectedSchool);
    const setSelectedSchoolList = useSetRecoilState(selectedSchool);
    const [posts, setPosts] = useState([]); // 현재 화면에 보여질 전체 게시물
    const [visiblePosts, setVisiblePosts] = useState([]); // 화면에 표시할 데이터
    const [page, setPage] = useState(1); // 현재 페이지
    const [selectedCategory, setSelectedCategory] = useState("졸업대장"); // 선택된 카테고리
    const observerRef = useRef(null); // IntersectionObserver 참조용
    const scrollTableRef = useRef(null); //스크롤 초기화 ref 생성
    const dataPerPage = 10;

    const categoryData = useMemo(() => ({
        졸업대장: GraduationRegisterData,
        학교일람: SchoolCatalogueData,
        회원명부: MemberDirectoryData,
        학교연감: YearBookData,
        학교사: SchoolHistoryData,
        기타: OtherData,
    }), []);

    const navigate = useNavigate();

    // 초기 데이터 로드 및 카테고리 변경 시 데이터 업데이트
    useEffect(() => {
        const data = categoryData[selectedCategory] || [];
        setPosts(data);
        setVisiblePosts(data.slice(0, dataPerPage));
        setPage(1);

        //카테고리 선택 시 스크롤 초기화
        if (scrollTableRef.current) {
            scrollTableRef.current.scrollTop = 0;
        }
    }, [selectedCategory, categoryData]);

    // 무한 스크롤 데이터 로드
    const loadMorePosts = useCallback((targetPage) => {
        const startIndex = (targetPage - 1) * dataPerPage;
        const endIndex = startIndex + dataPerPage;

        const morePosts = posts.slice(startIndex, endIndex);

        if (morePosts.length > 0) {
            setVisiblePosts((prev) => [...prev, ...morePosts]);
        }
    }, [posts, dataPerPage]);

    // IntersectionObserver 설정
    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setPage((prevPage) => prevPage + 1); // 다음 페이지 로드
                }
            },
            {threshold: 1.0, rootMargin : "20px", root: scrollTableRef.current}
        );

        const currentObserverRef = observerRef.current;

        if (currentObserverRef) {
            observer.observe(currentObserverRef);
        }

        return () => {
            if (currentObserverRef) {
                observer.unobserve(currentObserverRef); // 지역 변수 사용
            }
            observer.disconnect();
            setPosts(new Set());
            setPage(1);
        };
    }, []);

    // 페이지 변경 시 데이터 로드
    useEffect(() => {
        if (page > 1) {
            loadMorePosts(page);
        }
    }, [page, loadMorePosts]);

    const handleSchoolClick = (order) => {
        const exists = selectedSchoolList.some(school => school.id === order.school_id);

        const updatedList = exists
            ? [...selectedSchoolList] // 기존 순서 유지
            : [...selectedSchoolList, { id: order.school_id, name: order.primaryname, nameKo: order.primaryname_ko }];

        setSelectedSchoolList(updatedList);

        const idList = updatedList.map(school => school.id);

        // 배열에 넣어서 personId 보내기
        navigate(`/school/${idList.join(',')}`);
    };

    // 상세 이동 함수
    // const viewDetail = () => {
    //     // TODO: Detail page view logic
    //
    //     navigate("/emptyPage")
    // }

    return (
        <>
            <div id="container">
                <main id="contents">
                    <div className="sub-content">
                        <div className="archive-wrap">
                            <div className="cont-top">
                                <div className="w-set">
                                    <div className="title-wrap">
                                        <h2 className="title batang">기타 학교자료 <span style={{fontSize: "0.8em"}}>(자료 준비중...)</span></h2>
                                        <p className="text">졸업대장, 학교일람 등 교원 및 학생정보가 담긴 다양한 자료가 있습니다.</p>
                                    </div>
                                </div>
                            </div>

                            <div className="cont-btm">
                                <div className="archive-text-box">
                                    <div className="w-set">

                                        <div className="text-wrap">
                                            <p><strong>기타 학교자료</strong></p>
                                            <p className="text">일제강점기 학교들은 졸업대장, 회원명부, 학교연감, 학교사(학교 연혁지), 학교일람 등 여러 형식으로 교직원과 학생 명단을 제작했습니다. 이 명단들에는 재직･재학 연도, 직위, 전공, 졸업 후 직업, 주소, 본명과 창시 개명 후 이름 등 학교에 있었던 인물들에 대한 정보가 포함되어 있습니다. 이를 통해 어떠한 인물이 언제 어느 학교에 있었는지 파악할 수 있습니다.
                                            </p>
                                        </div>
                                        <div className="img-wrap">
                                            <div className="archive-slides">
                                                <ul className="slides-wrapper">
                                                    <li>
                                                        <div className="imgbox"><img src="/images/archive/schoolCatalogue_main.jpg" alt="기타 학교일람 등 이미지_졸업증서대장광주사범학교_졸업자명부_1939년이후" /></div>
                                                        <p className="caption">졸업증서대장광주사범학교 졸업자 명부 <br />(1939년 이후)</p>
                                                    </li>
                                                </ul>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="list-wrap table-contents">
                                    <div className="w-set">
                                        <div className="list-btm">
                                            <div className="table-wrap">
                                                <div className="category-wrap">
                                                    <div className="category-list">
                                                        {Object.keys(categoryData).map((category) => (
                                                            <button
                                                                key={category}
                                                                className={`category ${selectedCategory === category ? "on" : ""}`}
                                                                onClick={() => setSelectedCategory(category)}
                                                            >
                                                                {category}
                                                            </button>
                                                        ))}
                                                    </div>
                                                </div>

                                                <div className="scroll-table" ref={scrollTableRef}>
                                                    {visiblePosts.map((item, index) => (
                                                        <div className="list-row" key={index}>

                                                                <Link to="#" className="row-link disabled" onClick={(e) => {
                                                                    e.preventDefault();
                                                                    // TODO 상세 팝업 추가하기
                                                                    // window.$popup.template('#redbookViewPopup');
                                                                }}>
                                                                <div className="title-ko c-black bold">{item.book_title_ko}</div>
                                                                <div className="school">
                                                                    <button
                                                                        key={item.school_id}
                                                                        className="search-text"
                                                                        onClick={(e) => {
                                                                            handleSchoolClick(item);
                                                                        }}
                                                                    >
                                                                        {item.primaryname}
                                                                    </button>
                                                                </div>
                                                                </Link>

                                                        </div>
                                                    ))}
                                                    <div ref={observerRef}/>
                                                </div>
                                            </div>


                                            <div className="load-wrap type-full" style={{display: 'none'}}>
                                                <div className="load-box">
                                                    <div className="square"></div>
                                                    <div className="square"></div>
                                                    <div className="square"></div>
                                                    <div className="square"></div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </main>

                {/* 팝업 */}
                <div className="wbz-popup-cont is-template" id="redbookViewPopup">
                    <div className="popup-header">
                        <b className="popup-title">朝鮮總督府直屬機關&gt;勸業模範場&gt;朝鮮總督府農林學校</b>
                        <button type="button" className="popup-close" onClick={() => window.$popup.close()}>닫기</button>
                    </div>
                    <div className="popup-cont">
                        <div className="view-table">
                            {Array.from({ length: 14 }).map((_, index) => (
                                <div className="list-row" key={index}>
                                    <div className="paerson-name">
                                        <button type="button" className="search-text">李相龍 李相龍</button>
                                        <span>이상룡</span>
                                    </div>
                                    <div className="status-text">福岡縣八女郡川崎尋常高等小學校訓導福岡縣八女郡川崎尋常高等小學校訓導</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

export default SchoolCatalogue;